"use client";

import { Box, Flex, Grid, Text, Icon } from "@chakra-ui/react";
import { useTranslations } from "next-intl";
import { ScrollReveal } from "./ScrollReveal";
import { SectionLabel } from "./SectionLabel";
import { TbTargetArrow, TbLink, TbScale, TbChartBar } from "react-icons/tb";
import { ElementType } from "react";

const ICONS: ElementType[] = [TbTargetArrow, TbLink, TbScale, TbChartBar];

export function LandingEvalStrip() {
  const tHeader = useTranslations("landing.header");
  const tEval = useTranslations("landing.redesign.eval");
  const tAgent = useTranslations("landing.agent_showcase");
  const items = tAgent.raw("items") as Array<{
    title: string;
    description: string;
  }>;
  const pillars = items.slice(0, 4);

  return (
    <Box
      as="section"
      id="knoot-agent"
      aria-labelledby="landing-eval-heading"
      bg="white"
      border="1px solid"
      borderColor="neutral.200"
      rounded="4xl"
    >
      <Grid
        maxW="full"
        mx="auto"
        py={{ base: 5, md: 12 }}
        px={{ base: 4, md: 8, lg: 16 }}
        templateColumns={{ base: "1fr", md: "1fr 1fr" }}
        gap={{ base: 10, md: 10, lg: 20 }}
        alignItems="center"
      >
        <ScrollReveal>
          <Box>
            <SectionLabel>{tHeader("knoot-agent")}</SectionLabel>
            <Text
              as="h2"
              id="landing-eval-heading"
              fontFamily="heading"
              fontSize={{ base: "2xl", md: "4xl" }}
              fontWeight="semibold"
              letterSpacing="-1px"
              color="neutral.900"
              lineHeight={1.1}
              mb={3}
            >
              {tEval("title_line1")}
              <br />
              {tEval("title_line2")}
            </Text>
            <Text
              fontSize="15px"
              color="neutral.500"
              lineHeight={1.7}
              fontWeight="300"
              mt={3.5}
            >
              {tAgent("description")}
            </Text>
          </Box>
        </ScrollReveal>

        <ScrollReveal delay={0.2}>
          <Flex direction="column" gap={4}>
            {pillars.map((item, i) => (
              <Flex
                key={item.title}
                align="center"
                gap={3.5}
                p={{ base: 3.5, md: 4 }}
                rounded="3xl"
                border="1px solid"
                borderColor="neutral.200"
                bg="neutral.50"
                transition="all 0.25s"
                cursor="default"
                _hover={{
                  borderColor: "primary.500",
                  bg: "primary.50",
                  transform: "translateX(4px)",
                  color: "primary.600",
                }}
                color="neutral.600"
              >
                <Icon
                  id={`landing-eval-strip-icon-${i}`}
                  as={ICONS[i]}
                  color="inherit"
                  boxSize={6}
                  mx={2}
                  flexShrink={0}
                />
                <Box>
                  <Text
                    textStyle="sm"
                    fontWeight="bold"
                    color="neutral.800"
                    mb={0.5}
                  >
                    {item.title}
                  </Text>
                  <Text textStyle="xs" color="neutral.500">
                    {item.description}
                  </Text>
                </Box>
              </Flex>
            ))}
          </Flex>
        </ScrollReveal>
      </Grid>
    </Box>
  );
}
